import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { Customer, Organisation } from '../../services/api'
import { Sun, Moon, User } from 'lucide-react'

interface HeaderProps {
  user: any
  customer: Customer | null
  organisations: Organisation[]
  selectedOrgId: string
  onOrgChange: (orgId: string) => void
}

const Header: React.FC<HeaderProps> = ({
  user,
  customer,
  organisations,
  selectedOrgId,
  onOrgChange
}) => {
  const { logout } = useAuth()
  const { isDarkMode, toggleDarkMode } = useTheme()
  const [showUserMenu, setShowUserMenu] = useState(false)

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and org selector */}
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Phisheez</h1>
            
            {/* Organisation selector - only show if more than one */}
            {organisations.length > 1 ? (
              <select
                value={selectedOrgId}
                onChange={(e) => onOrgChange(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {organisations.map(org => (
                  <option key={org.organisation_id} value={org.organisation_id}>
                    {org.organisation_display_name}
                  </option>
                ))}
              </select>
            ) : organisations.length === 1 ? (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {organisations[0].organisation_display_name}
              </span>
            ) : null}
          </div>

          {/* Dark mode toggle and User menu */}
          <div className="flex items-center space-x-3">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-300 dark:border-gray-600"
              aria-label="Toggle dark mode"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-yellow-500" />
              ) : (
                <Moon className="h-5 w-5 text-blue-600" />
              )}
            </button>

            {/* User menu */}
            <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {user?.givenName?.[0] || user?.email?.[0] || 'U'}
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {user?.displayName || user?.email}
              </span>
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showUserMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-20 py-1 border border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                    <div className="font-medium">{user?.displayName || user?.email}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {customer?.subscription_status === 'trial' ? 'Trial' : 'Starter'}
                    </div>
                  </div>

                  <button
                    onClick={() => logout()}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
